import React from 'react'
import { FaTrash, FaEdit, FaSave, FaTimes } from 'react-icons/fa'
import UserAvatar from '../UI/UserAvatar'
import EmojiPicker from './EmojiPicker'
import CommentReactions from './CommentReactions'
import { useCommentEditor } from '../../hooks/useCommentEditor'
import { getCommentPermissions } from '../../utils/commentPermissions'

const CommentItem = ({
  comment,
  currentUserId,
  eventOwnerId,
  onEdit,
  onDelete,
  onEmojiReaction,
  onRemoveReaction
}) => {
  const {
    editingComment,
    editText,
    showEmojiPicker,
    setEditText,
    handleEditComment,
    handleSaveEdit,
    handleCancelEdit,
    handleEmojiSelect,
    toggleEmojiPicker,
    closeEmojiPicker
  } = useCommentEditor(comment, onEdit, onEmojiReaction)

  const { canEdit, canDelete } = getCommentPermissions(comment, currentUserId, eventOwnerId)

  return (
    <div className="flex gap-3">
      <UserAvatar user={comment.user} />

      <div className="flex-1 min-w-0">
        <div className="bg-white/10 rounded-lg p-3">
          <CommentHeader
            user={comment.user}
            comment={comment}
            canEdit={canEdit}
            canDelete={canDelete}
            isEditing={editingComment}
            onEdit={handleEditComment}
            onDelete={() => onDelete(comment._id)}
          />

          {editingComment ? (
            <EditCommentForm
              editText={editText}
              originalText={comment.text}
              onTextChange={setEditText}
              onSave={handleSaveEdit}
              onCancel={handleCancelEdit}
            />
          ) : (
            <>
              <p className="text-gray-200 text-sm break-words">
                {comment.text}
              </p>

              <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/10">
                <CommentReactions
                  reactions={comment.reactions}
                  currentUserId={currentUserId}
                  onReact={(emoji) => onEmojiReaction(comment._id, emoji)}
                  onRemoveReaction={() => onRemoveReaction(comment._id)}
                />

                <EmojiPicker
                  isOpen={showEmojiPicker}
                  onToggle={toggleEmojiPicker}
                  onEmojiSelect={handleEmojiSelect}
                  onClose={closeEmojiPicker}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const CommentHeader = ({ user, comment, canEdit, canDelete, isEditing, onEdit, onDelete }) => (
  <div className="flex items-center justify-between mb-1">
    <span className="text-white font-medium text-sm">
      {user?.username || user?.firstName || '[Deleted User]'}
    </span>
    <div className="flex items-center gap-2">
      <span className="text-gray-400 text-xs">
        {new Date(comment.createdAt).toLocaleDateString()}
        {comment.updatedAt && comment.updatedAt !== comment.createdAt && (
          <span className="ml-1 text-gray-500">(edited)</span>
        )}
      </span>
      {canEdit && !isEditing && (
        <button
          onClick={onEdit}
          className="text-blue-400 hover:text-blue-300 transition-colors"
          title="Edit comment"
        >
          <FaEdit className="text-xs" />
        </button>
      )}
      {canDelete && (
        <button
          onClick={onDelete}
          className="text-red-400 hover:text-red-300 transition-colors"
          title="Delete comment"
        >
          <FaTrash className="text-xs" />
        </button>
      )}
    </div>
  </div>
)

const EditCommentForm = ({ editText, originalText, onTextChange, onSave, onCancel }) => (
  <div className="space-y-2">
    <textarea
      value={editText}
      onChange={(e) => onTextChange(e.target.value)}
      className="w-full px-2 py-1 bg-white/5 border border-white/20 rounded text-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 resize-none"
      rows={2}
      maxLength={500}
      autoFocus
    />
    <div className="flex items-center justify-between">
      <span className="text-gray-500 text-xs">{editText.length}/500</span>
      <div className="flex gap-2">
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-300 transition-colors"
          title="Cancel"
        >
          <FaTimes className="text-xs" />
        </button>
        <button
          onClick={onSave}
          disabled={!editText.trim() || editText.trim() === originalText}
          className="text-green-400 hover:text-green-300 disabled:text-gray-500 transition-colors"
          title="Save"
        >
          <FaSave className="text-xs" />
        </button>
      </div>
    </div>
  </div>
)

export default CommentItem